import { useRef, useState } from 'react';
import { Box, CircularProgress, Stack, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Typography } from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';

import CONFIG from '@/config';
import AuthUserStore from '@/common/utils/AuthUser';
import { setLogin } from '@/views/Auth/hooks';

const useStyles = makeStyles(() => ({
  header: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '22px'
  },
  container: {
    padding: '16px 0px'
  },
  pinInput: {},
  pinInputField: {
    '& .MuiInputBase-input': {
      backgroundColor: 'transparent !important',
      textAlign: 'center',
      fontSize: '32px'
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        border: 'none'
      }
    },
    '& .MuiInput-underline:before': {
      borderBottom: '3px solid black'
    },
    '& .MuiInput-underline:hover:before': {
      borderBottom: '3px solid black !important'
    },
    '& .MuiInput-underline:after': {
      borderBottom: '3px solid black'
    }
  },
  status: {
    paddingTop: '8px',
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '-8px'
  },
  error: {
    color: '#E90000',
    fontSize: '14px',
    fontWeight: 400,
    textAlign: 'center'
  },
  loadingContainer: {
    marginTop: '4px'
  }
}));

const error_messages = {
  REQUESTED_STORE_ACCESS_DENIED:
    'User does not have access to the requested studio',
  PREVIOUS_STORE_ACCESS_DENIED:
    'User does not have access to the current studio'
};

export interface PinInputProps {
  onSuccess: () => void;
  setPinCallback?: (pin: string, isCancelled: boolean) => void;
  newStudioId?: number;
}

// if setPinCallback is set then instead of making an API call to pin-in
// it instead returns the pin entered via setPinCallback
const PinInput = ({
  onSuccess,
  newStudioId,
  setPinCallback
}: PinInputProps) => {
  const classes = useStyles();

  const [pin, setPin] = useState(Array(4).fill(''));
  const [isInvalid, setIsInvalid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentActiveIndex, setCurrentActiveIndex] = useState(0);
  const inputRefs = useRef([]);
  const { enqueueSnackbar } = useSnackbar();

  const handlePinChange = (value, index) => {
    if (value.length > 1) {
      return;
    }

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);

    if (value && index < pin.length - 1) {
      setCurrentActiveIndex(index + 1);
      inputRefs.current[index + 1].focus();
    }

    if (newPin.every(char => char !== '')) {
      handleSubmit(newPin.join('').toUpperCase());
    }
  };

  const handleKeyDown = event => {
    const index = parseInt(event.target.id.split('-')[1], 10);

    // Prevent key actions if the edited `TextField` is not the current active one
    if (index !== currentActiveIndex) {
      event.preventDefault();
      return;
    }

    // Handle Backspace (Delete)
    if (event.keyCode === 8) {
      if (index >= 0) {
        const newPin = [...pin];
        newPin[index] = '';
        setPin(newPin);
        if (index > 0 && pin[index] === '') {
          setCurrentActiveIndex(index - 1);
          inputRefs.current[index - 1].focus();
        }
      }
    }

    // Prevent Tab navigation
    if (event.keyCode === 9) {
      event.preventDefault();
    }
  };

  const handleClickFocus = () => {
    let index = pin.filter(char => char !== '').length;
    if (index >= pin.length) {
      index = pin.length - 1;
    }
    inputRefs.current[index].focus();
  };

  const handleReset = () => {
    setPin(Array(4).fill(''));
    setCurrentActiveIndex(0);
    inputRefs.current[0].focus();
  };

  const handleSubmit = async pinValue => {
    if (setPinCallback) {
      setPinCallback(pinValue, false);
      onSuccess();
    } else {
      setIsLoading(true);
      await axios
        .post(
          CONFIG.USERS_API_URL + '/auth/staff/pin-in',
          { pin: pinValue, storeId: newStudioId },
          {
            headers: {
              Authorization: `Bearer ${AuthUserStore.getAuthToken()}`
            },
            withCredentials: true
          }
        )
        .then((response: any) => {
          if (response.status === 200) {
            setIsInvalid(false);
            setLogin(response.data.data);
            onSuccess();
          }
        })
        .catch(err => {
          if (err?.response?.data?.error_code === 'INVALID_PIN') {
            setIsInvalid(true);
          }
          if (err?.response?.data?.error_code === 'MISSING_REFRESH_TOKEN') {
            const msg =
              error_messages[err?.response?.data?.error_code] ||
              'An unknown error occurred. Please log in again';
            enqueueSnackbar(msg, {
              variant: 'error'
            });
            AuthUserStore.logout();
          } else {
            console.log(err);
            const msg =
              error_messages[err?.response?.data?.error_code] ||
              'An unknown error occurred. Please sign out';
            enqueueSnackbar(msg, {
              variant: 'error'
            });
          }
          handleReset();
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  return (
    <Box className={classes.container}>
      <Stack className={classes.pinInput} direction="row" spacing={2}>
        {pin.map((digit, index) => (
          <TextField
            key={index}
            id={`pin-${index}`}
            value={digit}
            name="pin"
            variant="standard"
            className={classes.pinInputField}
            onChange={e => handlePinChange(e.target.value, index)}
            inputRef={el => (inputRefs.current[index] = el)}
            autoFocus={index === 0}
            autoComplete="off"
            onKeyDown={handleKeyDown} // Add handleKeyDown for key events
            onClick={handleClickFocus} // Add handleClickFocus for click events
            inputProps={{
              maxLength: 1,
              style: {
                WebkitTextSecurity: 'disc',
                MozTextSecurity: 'disc',
                textSecurity: 'disc'
              } as React.CSSProperties
            }}
          />
        ))}
      </Stack>
      <Box className={classes.status}>
        {isLoading && (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={24} color="primary" />
          </Box>
        )}
        {!isLoading && isInvalid && (
          <Typography className={classes.error}>Invalid PIN</Typography>
        )}
      </Box>
    </Box>
  );
};

export default PinInput;
